import React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import {
  Menu,
  X,
  User,
  LogOut,
  Car,
  Calendar,
  MessageCircle,
  Settings,
  ChevronDown,
  ShoppingCart,
  Crown,
  LayoutDashboard,
  AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import { useCheckinAvailable } from '../hooks/useCheckinAvailable';

const ONE_WORLD_LOGO = "/branding/oneworld-logo.png";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { user, logout, isAdmin, isMaster, isBusiness, isAuthenticated } = useAuth();
  const { available: checkinAvailable } = useCheckinAvailable();
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const { getNav, settings } = useSiteSettings();
  const nav = getNav();
  const maintenance = settings?.maintenance;

  const allNavLinks = [
    { name: nav.ueber_uns || 'Über uns', href: '/ueber-uns' },
    { name: nav.produkte || 'Produkte', href: '/produkte' },
    { name: nav.services || 'Preise & Services', href: '/services' },
    { name: nav.werkzeuge || 'Werkzeuge', href: '/werkzeuge' },
    { name: nav.my_world || 'My World', href: '/my-world' },
    { name: nav.kontakt || 'Kontakt', href: '/kontakt' },
  ];
  // Business users don't see MyWorld — that's a private-customer feature.
  const navLinks = isBusiness ? allNavLinks.filter(l => l.href !== '/my-world') : allNavLinks;

  const authNavLinks = [];

  return (
    <>
      {/* Maintenance Banner - prominent */}
      {maintenance?.enabled && (
        <div className="fixed top-0 left-0 right-0 z-header" data-testid="maintenance-banner">
          <div className="bg-amber-500 border-b-2 border-amber-600">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-center gap-4">
              <div className="animate-pulse">
                <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-black flex-shrink-0" />
              </div>
              <div className="text-center">
                <p className="text-sm sm:text-lg font-bold text-black tracking-wide uppercase">Wartungsmodus aktiv</p>
                <p className="text-xs sm:text-sm text-black/80 font-medium mt-0.5">{maintenance.message || 'Aktuell werden Wartungsarbeiten durchgeführt. Einige Dienste sind vorübergehend nicht erreichbar.'}</p>
              </div>
              <div className="animate-pulse">
                <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-black flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      )}
    <header 
      className={`fixed ${maintenance?.enabled ? 'top-[68px]' : 'top-0'} w-full z-header transition-all duration-300 ${
        scrolled 
          ? 'bg-[#161313]/95 backdrop-blur-xl border-b border-white/5' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={isBusiness ? '/business/dashboard' : '/'} className="flex items-center gap-3" data-testid="logo-link">
            <div className="flex flex-col">
              <img
                src={ONE_WORLD_LOGO}
                alt="One World"
                className="h-12 w-auto"
              />
              {isBusiness && (
                <span
                  className="text-[10px] font-bold tracking-[0.28em] uppercase leading-none mt-0.5 text-center"
                  style={{
                    color: '#b794ff',
                    textShadow: '0 0 12px rgba(183, 148, 255, 0.55)',
                    background: 'linear-gradient(90deg, #b794ff 0%, #d8b4ff 45%, #b794ff 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                  data-testid="business-badge"
                >
                  Business
                </span>
              )}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-3.5">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-[13px] font-medium text-white/80 hover:text-[#0094ff] transition-colors tracking-wide whitespace-nowrap"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {authNavLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-[13px] font-medium text-white/80 hover:text-[#0094ff] transition-colors tracking-wide whitespace-nowrap"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
            
            {isAuthenticated && checkinAvailable && (
              <Link
                to="/check-in"
                className="text-[13px] font-medium text-[#0094ff] hover:text-[#33a9ff] transition-colors tracking-wide whitespace-nowrap"
                data-testid="nav-checkin"
              >
                Check-In
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="hidden xl:flex items-center gap-2">
            <Link to="/termin">
              <Button 
                variant="outline"
                size="sm"
                className="border-[#0094ff] text-[#0094ff] hover:bg-[#0094ff] hover:text-white font-medium text-[13px]"
                data-testid="booking-btn"
              >
                Termin anfragen
              </Button>
            </Link>
            
            {isAuthenticated ? (
              <>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="flex items-center gap-1.5 text-white hover:text-[#0094ff] hover:bg-white/5"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-[13px] max-w-[100px] truncate">{user?.name?.split(' ')[0] || 'Profil'}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent sideOffset={18} collisionPadding={12} className="w-56 bg-[#231f1f] border-white/10 text-white">
                  {isBusiness ? (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/business/dashboard')} className="cursor-pointer hover:bg-white/5">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Business Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer hover:bg-white/5 text-red-400">
                        <LogOut className="w-4 h-4 mr-2" />
                        Abmelden
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer hover:bg-white/5">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profil')} className="cursor-pointer hover:bg-white/5">
                        <User className="w-4 h-4 mr-2" />
                        Mein Profil
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/konto')} className="cursor-pointer hover:bg-white/5">
                        <Settings className="w-4 h-4 mr-2" />
                        Persönliche Daten
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/fahrzeuge')} className="cursor-pointer hover:bg-white/5">
                        <Car className="w-4 h-4 mr-2" />
                        Meine Fahrzeuge
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/termine')} className="cursor-pointer hover:bg-white/5">
                        <Calendar className="w-4 h-4 mr-2" />
                        Meine Termine
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/chat')} className="cursor-pointer hover:bg-white/5">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Service Chat
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/bestellen')} className="cursor-pointer hover:bg-white/5">
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Bestellen
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer hover:bg-white/5 text-[#0094ff]">
                            <Settings className="w-4 h-4 mr-2" />
                            Admin Panel
                          </DropdownMenuItem>
                        </>
                      )}
                      {isMaster && (
                        <DropdownMenuItem onClick={() => navigate('/master')} className="cursor-pointer hover:bg-white/5 text-[#0094ff]">
                          <Crown className="w-4 h-4 mr-2" />
                          Master Panel
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer hover:bg-white/5 text-red-400">
                        <LogOut className="w-4 h-4 mr-2" />
                        Abmelden
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/business"
                  className="text-[12px] font-medium text-[#b794ff] hover:text-white transition-colors whitespace-nowrap tracking-wide"
                  data-testid="business-login-link"
                >
                  Für Betriebe
                </Link>
                <Link to="/login">
                  <Button
                    className="bg-[#0094ff] hover:bg-[#33a9ff] text-white font-medium px-6"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 text-white relative z-10"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - rendered via portal to escape stacking context */}
      {createPortal(
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="xl:hidden bg-[#161313] border-t border-white/5 fixed top-20 left-0 right-0 bottom-0 z-overlay-nav overflow-y-auto"
            >
              <nav className="py-4 space-y-1 pb-20">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-white/80 hover:text-[#0094ff] hover:bg-white/5 transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
                {isAuthenticated && authNavLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-3 text-white/80 hover:text-[#0094ff] hover:bg-white/5 transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-[#0094ff] font-medium hover:bg-white/5"
                    >
                      Dashboard
                    </Link>
                    {checkinAvailable && (
                      <Link
                        to="/check-in"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 text-[#0094ff] hover:bg-white/5"
                      >
                        Check-In
                      </Link>
                    )}
                    <Link
                      to="/chat"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-white/80 hover:bg-white/5"
                    >
                      Service Chat
                    </Link>
                    <Link
                      to="/bestellen"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-white/80 hover:bg-white/5"
                    >
                      Bestellen
                    </Link>
                    <Link
                      to="/profil"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-white/80 hover:bg-white/5"
                    >
                      Mein Profil
                    </Link>
                    <Link
                      to="/konto"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-white/80 hover:bg-white/5"
                    >
                      Persönliche Daten
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 text-[#0094ff] hover:bg-white/5"
                      >
                        Admin Panel
                      </Link>
                    )}
                    {isMaster && (
                      <Link
                        to="/master"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-3 text-[#0094ff] hover:bg-white/5"
                      >
                        Master Panel
                      </Link>
                    )}
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-3 text-red-400 hover:bg-white/5"
                    >
                      Abmelden
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-[#0094ff] font-medium"
                    >
                      Login
                    </Link>
                    <Link
                      to="/business"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-[#b794ff] font-medium border-t border-white/5"
                      data-testid="mobile-business-login-link"
                    >
                      Für Betriebe
                    </Link>
                  </>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
    </>
  );
};

export default Header;
